import { z } from "zod";

/**
 * sessionStorage-backed snapshot of the buy form, written immediately before
 * the SumUp redirect and read on Purchase.vue mount.
 *
 * Why sessionStorage (not localStorage):
 *   - per-tab scope matches the user's mental model — closing the tab ends
 *     the attempt
 *   - survives the SumUp round-trip (same tab, same origin) so when a customer
 *     aborts or fails and clicks "Zurück zum Ticketkauf", the form rehydrates
 *
 * The /return page clears the draft on `status === "completed"` so a fresh
 * visit after a successful purchase doesn't pre-fill last time's cart.
 *
 * The key is versioned so a future schema change doesn't try to parse old
 * snapshots — bump the suffix and old data is silently ignored.
 */
const STORAGE_KEY = "evka.buy.draft.v1";

const buyDraftSchema = z.object({
  eventId: z.string().min(1),
  email: z.string().min(1),
  isMember: z.boolean(),
  adultQty: z.number().int().min(0).max(50),
  childQty: z.number().int().min(0).max(50),
});

export type BuyDraft = z.infer<typeof buyDraftSchema>;

function getStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function saveBuyDraft(draft: BuyDraft): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // QuotaExceeded / disabled storage — silently skip; the form still works
    // without restore.
  }
}

/**
 * Load and validate. Returns null when nothing is stored, when the stored
 * payload doesn't parse, or when the snapshot is for a different event than
 * the page is currently rendering.
 */
export function loadBuyDraft(expectedEventId: string): BuyDraft | null {
  const storage = getStorage();
  if (!storage) return null;
  const raw = storage.getItem(STORAGE_KEY);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
  const result = buyDraftSchema.safeParse(parsed);
  if (!result.success) {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
  if (result.data.eventId !== expectedEventId) return null;
  return result.data;
}

export function clearBuyDraft(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
